import * as React from "react";
import queryString from "query-string";
import { ClientStatus, IACLEmail, IClient } from "intake_shared/src/types";
import { v4 as uuidv4 } from "uuid";
import { TSaveClient } from "../../../clientUiTypes";
import { useToasts } from "react-toast-notifications";
import { useHistory, useLocation } from "react-router-dom";
import { AclContext } from "../../../../../../contexts/AclContext";
import { createNewClient } from "../../../../../../store/client";
import { sendVerificationTeamNotification } from "../../../../../../utils/queue_email";
import store from "../../../../../../store";
import { Dropdown } from "../../../../bits/Dropdown/Dropdown";
import AssignClient from "./AssignClient/AssignClient";
import MoveIcon from "../../../../icons/Move";
import { Button } from "../../../../bits/Button/Button";
import DuplicateChart from "./DuplicateChart/DuplicateChart";
import XCircleIcon from "../../../../icons/XCircle";
import TrashIcon from "../../../../icons/Trash";
import MoveClientToFollowUp from "./MoveClientToFollowUp/MoveClientToFollowUp";
import styles from "./EditClientOptions.module.scss";
import ChevronDownIcon from "../../../../icons/ChevronDown";
import SaveIcon from "../../../../icons/Save";
import { AlertDialog } from "../../../../bits/AlertDialog/AlertDialog";
import { useChartLock } from "../../../../../../contexts/ChartLockContext";
import { validateAllFees } from "../../fees/FeesForm/FeesForm";
import { CacheContext } from "../../../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../../../utils/intakeEmailSignature";
import {
    applyExplicitGuardianChartChangesToAttendees,
    getAppointmentHeldValidationMessage,
} from "../../../../../../utils/guardian";
import { hasFeeChanges } from "../../../../../../store/feeRevision";

interface EditClientOptionsProps {
    client: IClient;
    dirtyGuardianFields: Set<keyof IClient>;
    acl?: IACLEmail;
    isInactiveClient: boolean;
    allStatusesToShow: ClientStatus[];
    setActiveKey: React.Dispatch<React.SetStateAction<string>>;
    onSaveClient: TSaveClient;
    currentActiveTabStatus?: ClientStatus | null;
}

export default function EditClientOptions({
    client,
    dirtyGuardianFields,
    acl,
    isInactiveClient,
    allStatusesToShow,
    onSaveClient,
    setActiveKey,
}: EditClientOptionsProps) {
    const { addToast } = useToasts();
    const history = useHistory();
    const { releaseChartLock } = useChartLock();
    const browserLocation = useLocation();
    const olc = React.useContext(CacheContext);
    const intakeSignature = getIntakeEmailSignature(olc);
    const parsedQueryParams = queryString.parse(browserLocation.search || "");
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const [showMoveClientModal, setShowMoveClientModal] = React.useState(false);
    const [showBlacklistDialog, setShowBlacklistDialog] = React.useState(false);
    const [showDeleteClientDialog, setShowDeleteClientDialog] =
        React.useState(false);
    const lastUpdatedBy = acl?.email || loggedInUserEmail || "";
    const feeChangesDirty = hasFeeChanges(client);

    const addClient = async () => {
        const id = uuidv4();

        const newClient: IClient = {
            ...client,
            id,
            status: ClientStatus.INCOMPLETE_INQUIRY,
            status_history: {
                "Incomplete Inquiry": new Date(),
            },
            date_of_birth: client?.date_of_birth ? client?.date_of_birth : null,
            confirmed_bookings: [],
            held_bookings: [],
            appointment_history: [],
            date_updated: new Date(),
        };
        await createNewClient(newClient, loggedInUserEmail as string);
        onSaveClient(newClient);
    };

    const handleMoveClient = async (status) => {
        const updatedClient = {
            ...client,
            [ClientStatus.FOLLOWUP]: {
                start_date: client[ClientStatus.FOLLOWUP]?.start_date || "",
                end_date: "",
                ending_date: "",
                followup_notes: client[ClientStatus.FOLLOWUP]?.followup_notes || "",
                status_after_end_date:
                    client[ClientStatus.FOLLOWUP]?.status_after_end_date || "",
            },
            status,
        };

        if (status === ClientStatus.FOLLOWUP) {
            setShowMoveClientModal(true);
            return;
        }
        if (status === ClientStatus.APPT_HELD) {
            const heldValidationMessage =
                getAppointmentHeldValidationMessage(client);
            if (heldValidationMessage) {
                addToast(heldValidationMessage, {
                    appearance: "error",
                    autoDismiss: false,
                });
                return;
            }
        }
        try {
            if (isInactiveClient) {
                await store.updateArchivedClient(
                    client.id,
                    {
                        ...updatedClient,
                        last_updated_by: lastUpdatedBy,
                    },
                    feeChangesDirty
                );
            } else {
                await store.saveClientData(
                    updatedClient,
                    lastUpdatedBy,
                    true,
                    feeChangesDirty
                );
            }
            switch (status) {
                case ClientStatus.INS_VERIFICATION:
                    sendVerificationTeamNotification(updatedClient, intakeSignature);
                    break;
                default:
                    break;
            }
            addToast(`Client moved to ${status}`, {
                appearance: "success",
            });
            onSaveClient(updatedClient);
            setActiveKey(status);

            // Release chart lock after moving client
            if (client.id && parsedQueryParams.client_type !== "new_client") {
                await releaseChartLock(client.id);
            }
        } catch (ex) {
            console.log(ex);
            addToast(`Error occured, please contact your administrator`, {
                appearance: "error",
            });
        }
    };

    const handleSaveUpdates = async () => {
        if (feeChangesDirty) {
            const feeValidation = validateAllFees(client);

            if (!feeValidation.isValid) {
                addToast(
                    `Cannot save: Invalid fee format detected. Please fix the following fees: ${feeValidation.invalidFees.join(
                        ", "
                    )}.
                     Accepted format examples: 120x7; 90 (or 120x7; $90), 80x5; 15 TBD (or 80x5; $15 TBD), 180x14; 130TBD, 120TBD (or 120 TBD), 25, 455x1; 255, 655x1; 455 UDM, then 80, 165x3; 160.89 UDM, then 32 (all Psych Fee formats accept decimals), Kap Fee formats: 200; 100; 1000X2 THEN 200, 200; 100; 1000, NOT ACCEPTED (for INN fees)`,
                    {
                        appearance: "error",
                        autoDismiss: false,
                    }
                );
                return;
            }
        }

        try {
            if (isInactiveClient) {
                const result = await store.updateArchivedClient(
                    client.id,
                    {
                        ...client,
                        last_updated_by: lastUpdatedBy,
                    },
                    feeChangesDirty
                );
                addToast(
                    result.reactivated
                        ? "Client reactivated"
                        : "Completed client updated",
                    {
                        appearance: "success",
                    }
                );
                onSaveClient(client);
            } else if (parsedQueryParams.client_type === "new_client") {
                if (
                    client.email === "" ||
                    client.phone_no === "" ||
                    client.first_name === "" ||
                    client.last_name === ""
                ) {
                    addToast(
                        "One or more of the following fields is missing: first name, last name, email or phone number",
                        {
                            appearance: "error",
                        }
                    );
                    return;
                }
                await addClient();
                addToast("Client created successfully", {
                    appearance: "success",
                });
            } else {
                if (
                    client.email === "" ||
                    client.phone_no === "" ||
                    client.first_name === "" ||
                    client.last_name === ""
                ) {
                    addToast(
                        "One or more of the following fields is missing: first name, last name, email or phone number",
                        {
                            appearance: "error",
                        }
                    );
                    return;
                }
                const clientToSave = applyExplicitGuardianChartChangesToAttendees(
                    dirtyGuardianFields,
                    client
                );
                await store.saveClientData(
                    clientToSave,
                    lastUpdatedBy,
                    false,
                    feeChangesDirty
                );

                addToast("Updates saved", {
                    appearance: "success",
                });
                onSaveClient(clientToSave);
            }

            // Release chart lock after saving (only if not adding new client)
            if (client.id && parsedQueryParams.client_type !== "new_client") {
                await releaseChartLock(client.id);
            }
        } catch (ex) {
            console.log(ex);
            addToast(`Error occured, please contact your administrator`, {
                appearance: "error",
            });
        }
    };

    return (
        <div className={styles.actionsWrapper}>
            <AlertDialog
                open={showBlacklistDialog}
                onClose={() => setShowBlacklistDialog(false)}
                onSaveClick={async () => {
                    setShowBlacklistDialog(false);
                    await store.updateClientData({
                        client_id: client.id,
                        changes: {
                            is_blacklisted: !client.is_blacklisted,
                        },
                        last_updated_by: acl?.email!,
                    });
                    onSaveClient(client);
                }}
                onCancelClick={() => setShowBlacklistDialog(false)}
                saveText={client.is_blacklisted ? "Undo" : "Blacklist"}
                title={client.is_blacklisted ? "Undo blacklist" : "Blacklist client"}
                description={
                    client.is_blacklisted
                        ? "Are you sure you want to undo the blacklist?"
                        : "Are you sure you want to blacklist this client?"
                }
            />

            <AlertDialog
                open={showDeleteClientDialog}
                onClose={() => setShowDeleteClientDialog(false)}
                onSaveClick={async () => {
                    try {
                        await store.deleteClient(client.id);
                        addToast("Chart deleted successfully", {
                            appearance: "success",
                        });
                        onSaveClient(client);
                        history.goBack();
                    } catch (ex) {
                        console.log(ex);
                        addToast(
                            `Error occured, please contact your administrator`,
                            {
                                appearance: "error",
                            }
                        );
                    }
                }}
                title="Delete Client"
                description="Are you sure you want to delete this client?"
                saveText="Delete"
                isDeleting
                onCancelClick={() => setShowDeleteClientDialog(false)}
            />
            <Dropdown>
                <Dropdown.Trigger>
                    <Button
                        mode="outline"
                        iconLeft={<MoveIcon />}
                        iconRight={<ChevronDownIcon stroke="currentColor" />}
                    >
                        Move client to
                    </Button>
                </Dropdown.Trigger>
                <Dropdown.Content className={styles.dropdown}>
                    {allStatusesToShow.map((status) => (
                        <Dropdown.Item
                            onClick={() => handleMoveClient(status)}
                            key={`move-to-${status}`}
                        >
                            {status}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Content>
            </Dropdown>
            <DuplicateChart client={client} onSaveClient={onSaveClient} />
            <AssignClient
                client={client}
                onSaveClient={onSaveClient}
                setActiveKey={setActiveKey}
            />

            {client.on_waitlist && (
                <Button
                    mode="outline"
                    onClick={async () => {
                        onSaveClient(client);
                        await store.updateClientData({
                            client_id: client.id,
                            changes: {
                                on_waitlist: false,
                            },
                            last_updated_by: acl?.email!,
                        });
                        addToast("Client removed from waitlist", {
                            appearance: "success",
                        });
                    }}
                >
                    Remove from waitlist
                </Button>
            )}

            <div className={styles.divider} />
            <Button
                hidden={!Boolean(acl?.acl?.super_admin)}
                disabled={isInactiveClient}
                variant="secondary"
                size={client.is_blacklisted ? "small" : "icon"}
                className={styles.blacklist_btn}
                title={client.is_blacklisted ? "Undo blacklist" : "Blacklist client"}
                aria-label={
                    client.is_blacklisted ? "Undo blacklist" : "Blacklist client"
                }
                onClick={() => {
                    setShowBlacklistDialog(true);
                }}
            >
                {client.is_blacklisted ? "Undo blacklist" : <XCircleIcon />}
            </Button>

            {(client.status === ClientStatus.INQUIRY ||
                client.status === ClientStatus.INCOMPLETE_INQUIRY) && (
                <Button
                    variant="danger"
                    mode="outline"
                    size="icon"
                    title="Delete client"
                    aria-label="Delete client"
                    onClick={() => {
                        setShowDeleteClientDialog(true);
                    }}
                >
                    <TrashIcon stroke="currentColor" />
                </Button>
            )}

            <Button
                iconLeft={<SaveIcon />}
                onClick={handleSaveUpdates}
                className={styles.saveButton}
            >
                Save updates
            </Button>
            <MoveClientToFollowUp
                showMoveClientModal={showMoveClientModal}
                setShowMoveClientModal={setShowMoveClientModal}
                client={client}
                isInactiveClient={isInactiveClient}
                onSaveClient={onSaveClient}
            />
        </div>
    );
}
